import React from "react";
import { getStudent, signOutStudent } from "../Services/studentAuth";

/**
 * ONE definition of "who is signed in".
 *
 * There are two records: the real student account written by the phone-OTP
 * flow (`ulearnStudent`, Services/studentAuth) and the lightweight
 * `ulearn-tt-user` record the header/Tests pages write. Each screen used to
 * read only one of them, so the sidebar could show the account while the
 * header still offered "Sign in". Everything reads through here instead.
 */
export const TT_USER_KEY = "ulearn-tt-user";
export const AUTH_EVENT = "ulearn:auth";

const readLocal = () => {
  try {
    return JSON.parse(localStorage.getItem(TT_USER_KEY));
  } catch {
    return null;
  }
};

/** @returns {{name: string, phone: string}|null} */
export function readSessionUser() {
  const local = readLocal();
  const student = getStudent();
  if (!local && !student) return null;
  const name = (
    (local && local.name) ||
    (student && (student.name || student.fullName)) ||
    ""
  ).trim();
  const phone = (local && local.phone) || (student && student.phone) || "";
  return { name, phone };
}

/** What to show for a user with no name on file (phone-only accounts). */
export const displayNameOf = (user) =>
  (user && user.name) || (user && user.phone) || "Student";

/** First letter for an avatar, or null when there is no name to take one from. */
export const initialOf = (user) =>
  user && user.name ? user.name[0].toUpperCase() : null;

/** Tell every mounted screen that sign-in state changed. */
export function notifyAuthChange() {
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export function saveSessionUser(user) {
  localStorage.setItem(TT_USER_KEY, JSON.stringify(user));
  notifyAuthChange();
}

/** Sign out of both records — clearing one alone leaves the other signed in. */
export function clearSessionUser() {
  localStorage.removeItem(TT_USER_KEY);
  signOutStudent();
  notifyAuthChange();
}

/** Live view of the signed-in user, kept in step across screens and tabs. */
export function useSessionUser() {
  const [user, setUser] = React.useState(readSessionUser);
  React.useEffect(() => {
    const sync = () => setUser(readSessionUser());
    window.addEventListener(AUTH_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(AUTH_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return [user, setUser];
}
